import type { DependencyGraph } from 'store/journey/journey.type';
import type { RuleStateItem } from 'store/rule/rule.type';
import { isEmpty } from 'lodash';

/**
 * Pixel dimensions for ELK layout — must match `_flow-node-layout.scss`, which is the
 * source of truth for BaseNode, OptionalNodeWrapper, and FlowStartNode styles.
 */
export const FLOW_NODE_BASE_WIDTH = 145;
export const FLOW_NODE_BASE_HEIGHT = 158;
export const FLOW_NODE_OPTIONAL_WRAPPER_WIDTH = 175;
export const FLOW_NODE_OPTIONAL_WRAPPER_HEIGHT = 208;
export const FLOW_START_NODE_WIDTH = 155;
export const FLOW_START_NODE_HEIGHT = 35;

/**
 * Journey lock + dependency graph inputs used to decide whether optional push/content/SMS/email
 * nodes render `OptionalNodeWrapper` (and matching ELK dimensions).
 */
export type FlowOptionalWrapperContext = {
  dependencyGraph?: DependencyGraph;
  isJourneyLocked: boolean;
};

export type FlowNodeLayoutContext = FlowOptionalWrapperContext & {
  hasActivationDate: boolean;
};

/** Matches TargetAudienceNode “audience locked” (view-only / no optional shell). */
export function isTargetAudienceLocked(
  rule: RuleStateItem | null | undefined,
  isJourneyLocked: boolean,
  hasActivationDate: boolean,
): boolean {
  const isProductionRule = !!rule?.visibility && rule.visibility !== 'draft';
  return isJourneyLocked || hasActivationDate || isProductionRule;
}

/** Matches TargetAudienceNode: OptionalNodeWrapper when optional and audience is not locked. */
export function targetAudienceUsesOptionalWrapper(
  rule: RuleStateItem | null | undefined,
  ctx: Pick<FlowNodeLayoutContext, 'isJourneyLocked' | 'hasActivationDate'>,
): boolean {
  if (rule && !rule.isOptional) {
    return false;
  }
  return !isTargetAudienceLocked(rule, ctx.isJourneyLocked, ctx.hasActivationDate);
}

/** Matches PushNode / ContentNode / SMSNode / EmailNode optional shell rules. */
export function actionUsesOptionalWrapper(
  refId: string,
  isOptional: boolean | undefined,
  ctx: FlowOptionalWrapperContext,
): boolean {
  if (ctx.isJourneyLocked || !isOptional) {
    return false;
  }
  return isEmpty(ctx.dependencyGraph?.[refId]?.isRefBy);
}
